import { comparisonCriteriaData } from '../data/concepts-data.js';

export function renderComparisonView() {
  return `
    <section class="view-section" id="view-comparison">
      <div class="hero-card">
        <span class="hero-tag">Phase 05 Decision &amp; Synthesis Matrix</span>
        <h2>Concept Evaluation &amp; Strategic Decision Framework</h2>
        <p style="max-width: 800px; font-size: var(--text-lg); margin-bottom: 0; color: var(--text-secondary);">
          A multi-variable comparison evaluating the three brand concepts against strategic positioning, demographic alignment, accessibility, liturgy adaptation, and operational scalability.
        </p>
      </div>

      <!-- Quick Summary Cards -->
      <div class="grid-3" style="margin-bottom: var(--space-xl);">
        <div class="card" style="border-top: 4px solid #3B28CC;">
          <div class="mono" style="font-size: 11px; color: #3B28CC; font-weight: 700;">CONCEPT 01</div>
          <h4 style="font-size: var(--text-lg); margin-top: 4px; margin-bottom: 8px; color: var(--text-primary);">Bold Urban Activism</h4>
          <p style="font-size: var(--text-xs); line-height: 1.6; color: var(--text-secondary); margin-bottom: var(--space-md);">
            Best for maximizing civic visibility, youth engagement, LGBTQIA+ advocacy, and energetic street-level cultural resonance.
          </p>
          <div style="background: var(--bg-surface-elevated); padding: 8px; border-radius: var(--radius-sm); font-size: 11px; color: var(--text-primary);">
            <strong>Archetype:</strong> The Civic Disruptor
          </div>
        </div>

        <div class="card" style="border-top: 4px solid #C85A32;">
          <div class="mono" style="font-size: 11px; color: #C85A32; font-weight: 700;">CONCEPT 02</div>
          <h4 style="font-size: var(--text-lg); margin-top: 4px; margin-bottom: 8px; color: var(--text-primary);">Grounded Sanctuary</h4>
          <p style="font-size: var(--text-xs); line-height: 1.6; color: var(--text-secondary); margin-bottom: var(--space-md);">
            Best for trauma-informed healing, family hospitality, contemplative depth, and creating a peaceful harbor from urban burnout.
          </p>
          <div style="background: var(--bg-surface-elevated); padding: 8px; border-radius: var(--radius-sm); font-size: 11px; color: var(--text-primary);">
            <strong>Archetype:</strong> The Restorative Table Host
          </div>
        </div>

        <div class="card" style="border-top: 4px solid #D4AF37;">
          <div class="mono" style="font-size: 11px; color: #D4AF37; font-weight: 700;">CONCEPT 03</div>
          <h4 style="font-size: var(--text-lg); margin-top: 4px; margin-bottom: 8px; color: var(--text-primary);">Dynamic Neo-Liturgical</h4>
          <p style="font-size: var(--text-xs); line-height: 1.6; color: var(--text-secondary); margin-bottom: var(--space-md);">
            Best for intellectual seekers, creatives, and architects seeking transcendent beauty, open communion, and sacred modernism.
          </p>
          <div style="background: var(--bg-surface-elevated); padding: 8px; border-radius: var(--radius-sm); font-size: 11px; color: var(--text-primary);">
            <strong>Archetype:</strong> The Sacred Architect
          </div>
        </div>
      </div>

      <!-- Comprehensive Categorized Criteria Matrix -->
      <div class="card" style="margin-bottom: var(--space-2xl); overflow-x: auto;">
        <div class="card-header">
          <div>
            <h3 style="font-size: var(--text-xl); color: var(--text-primary);">10-Variable Strategic Scorecard</h3>
            <p style="font-size: var(--text-sm); color: var(--text-secondary); margin-bottom: 0;">
              Side-by-side analysis across research, design, production, and cultural criteria.
            </p>
          </div>
          <span class="mono" style="font-size: var(--text-xs); color: var(--accent-primary);">Comparative Grid</span>
        </div>

        <table class="comparison-table" style="width: 100%; border-collapse: collapse; min-width: 700px;">
          <thead>
            <tr style="border-bottom: 2px solid var(--border-subtle); text-align: left;">
              <th style="padding: 12px; font-size: var(--text-xs); color: var(--text-tertiary); text-transform: uppercase;">Evaluation Dimension</th>
              <th style="padding: 12px; font-size: var(--text-xs); color: #3B28CC; text-transform: uppercase;">01: Bold Activism</th>
              <th style="padding: 12px; font-size: var(--text-xs); color: #C85A32; text-transform: uppercase;">02: Grounded Sanctuary</th>
              <th style="padding: 12px; font-size: var(--text-xs); color: #D4AF37; text-transform: uppercase;">03: Neo-Liturgical</th>
            </tr>
          </thead>
          <tbody>
            ${comparisonCriteriaData.map(group => `
              <tr style="background: var(--bg-surface-elevated);">
                <td colspan="4" style="padding: 10px 12px; font-weight: 700; font-size: var(--text-xs); color: var(--text-primary); text-transform: uppercase; letter-spacing: 1px;">
                  📌 ${group.category}
                </td>
              </tr>
              ${group.items.map(item => `
                <tr style="border-bottom: 1px solid var(--border-subtle); transition: background 0.2s ease;">
                  <td style="padding: 14px 12px; font-size: var(--text-sm); font-weight: 600; color: var(--text-primary); vertical-align: top;">
                    ${item.dimension}
                  </td>
                  <td style="padding: 14px 12px; font-size: var(--text-xs); color: var(--text-secondary); line-height: 1.5; vertical-align: top; border-left: 1px solid var(--border-subtle);">
                    ${item.c1}
                  </td>
                  <td style="padding: 14px 12px; font-size: var(--text-xs); color: var(--text-secondary); line-height: 1.5; vertical-align: top; border-left: 1px solid var(--border-subtle);">
                    ${item.c2}
                  </td>
                  <td style="padding: 14px 12px; font-size: var(--text-xs); color: var(--text-secondary); line-height: 1.5; vertical-align: top; border-left: 1px solid var(--border-subtle);">
                    ${item.c3}
                  </td>
                </tr>
              `).join('')}
            `).join('')}
          </tbody>
        </table>
      </div>

      <!-- Strategic Decision Framework Paths -->
      <div class="card" style="border: 2px solid var(--border-subtle);">
        <div class="card-header">
          <div>
            <h3 style="font-size: var(--text-xl); color: var(--text-primary);">Implementation Decision Pathways</h3>
            <p style="font-size: var(--text-sm); color: var(--text-secondary); margin-bottom: 0;">
              Two recommended strategic pathways for UVC Church Council and pastoral leadership.
            </p>
          </div>
          <span class="hero-tag" style="background: var(--accent-primary); color: #FFFFFF;">DECISION ROADMAP</span>
        </div>

        <div class="grid-2" style="gap: var(--space-xl);">
          <div style="background: var(--bg-surface-elevated); padding: var(--space-lg); border-radius: var(--radius-md); border-left: 4px solid var(--accent-primary);">
            <div class="mono" style="font-size: 11px; color: var(--accent-primary); font-weight: 700;">PATHWAY A</div>
            <h4 style="font-size: var(--text-lg); color: var(--text-primary); margin-top: 4px; margin-bottom: var(--space-xs);">
              Select Single Pure Direction
            </h4>
            <p style="font-size: var(--text-xs); color: var(--text-secondary); line-height: 1.6; margin-bottom: var(--space-sm);">
              Commit fully to one distinct creative vector to achieve the highest possible brand focus and immediate market recognition.
            </p>
            <ul style="font-size: var(--text-xs); color: var(--text-primary); line-height: 1.6; margin-left: var(--space-md); margin-bottom: 0;">
              <li><strong>Choose C1</strong> for maximum activist energy and youth appeal.</li>
              <li><strong>Choose C2</strong> for deep restorative healing and intergenerational community.</li>
              <li><strong>Choose C3</strong> for architectural prestige and progressive sacramental depth.</li>
            </ul>
          </div>

          <div style="background: var(--bg-surface-elevated); padding: var(--space-lg); border-radius: var(--radius-md); border-left: 4px solid #00C853;">
            <div class="mono" style="font-size: 11px; color: #00C853; font-weight: 700;">PATHWAY B (RECOMMENDED SYNTHESIS)</div>
            <h4 style="font-size: var(--text-lg); color: var(--text-primary); margin-top: 4px; margin-bottom: var(--space-xs);">
              The Strategic Hybrid Framework
            </h4>
            <p style="font-size: var(--text-xs); color: var(--text-secondary); line-height: 1.6; margin-bottom: var(--space-sm);">
              Synthesize the highest-scoring dimensions of each concept into a multi-layered brand system:
            </p>
            <ul style="font-size: var(--text-xs); color: var(--text-primary); line-height: 1.6; margin-left: var(--space-md); margin-bottom: 0;">
              <li><strong>Visual Framework</strong>: Sacred Modernism &amp; Deep Navy/Brass (from Concept 3).</li>
              <li><strong>Public Voice</strong>: Bold Prophetic Justice &amp; Kinetic Contrast (from Concept 1).</li>
              <li><strong>Hospitality Experience</strong>: Warm Restorative Table &amp; Organic Textures (from Concept 2).</li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Proceed to Official Ballot CTA -->
      <div class="card" style="margin-top: var(--space-2xl); background: linear-gradient(135deg, rgba(30, 27, 75, 0.7) 0%, rgba(15, 23, 42, 0.95) 100%); border: 2px solid var(--border-accent); display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: var(--space-lg); padding: var(--space-2xl);">
        <div>
          <span class="hero-tag" style="background: rgba(99, 102, 241, 0.2); color: #a5b4fc; margin-bottom: 6px;">Next Step</span>
          <h3 style="margin: 0 0 4px 0; font-size: var(--text-2xl); color: var(--text-primary);">Ready to Cast Your Official Recommendation?</h3>
          <p style="margin: 0; font-size: var(--text-sm); color: var(--text-secondary); max-width: 650px;">
            Submit your ranked-choice vote, identify campus collateral needs, and share your wisdom with the UVC Church Council and staff.
          </p>
        </div>
        <button class="btn btn-primary" data-tab="ballot" style="background: var(--accent-primary); color: #FFFFFF; font-size: var(--text-base); padding: 12px 28px; font-weight: 700;">
          🗳️ Open Official Ballot &rarr;
        </button>
      </div>
    </section>
  `;
}
